import { Injectable } from '@angular/core';
import { CampaignInterface, CampaignSystemTabInterface } from 'app/models/campaign.model';
import { DatasetInterface } from 'app/models/dataset.model';
import { Resource } from 'app/models/resource.model';
import { DateRangeInterface } from 'app/modules/common/datetime/dateslots/dateslot.interface';
import {
  TimeslotInterface,
  TimeslotsCalendarInterface,
  WeeklySchedulingInterface,
} from 'app/modules/common/timeslot/timeslot.interface';
import { PlaylistItemInterface, PlaylistItemNestedInterface } from 'app/modules/editorial/editorial.interface';
import { DataTransformAbstractPost } from 'app/modules/global/classes/data.transform.abstract.down';
import { FluidDatePipe } from 'app/modules/global/pipes/fluidDate.pipe';

@Injectable({
  providedIn: 'root',
})
export class CampaignDataTransformPost extends DataTransformAbstractPost {
  constructor(protected _fluidDatePipe: FluidDatePipe) {
    super();
  }

  transform(campaign: CampaignInterface): CampaignInterface {
    campaign.folder = this.mapId(campaign.folder);
    campaign.circuits = this.mapIds(campaign.circuits);
    campaign.systems = this.mapIds(campaign.systems);
    campaign.kinds = this.mapIds(campaign.kinds);
    campaign.groups = this.mapIds(campaign.groups);

    //* this._normalizeRange(campaign.range);
    this._normalizeResources(campaign.resources);
    this._normalizeTabs(campaign.tabs);

    campaign.version = 'v2';
    return campaign;
  }

  private _normalizeTabs(tabs: CampaignSystemTabInterface[]) {
    if (!Array.isArray(tabs)) {
      return this;
    }
    tabs.forEach((tab: CampaignSystemTabInterface) => {
      tab.system = this.mapId(tab.system);
      this._normalizeResources(tab.resources);
    });
  }

  private _normalizeResources(resources: PlaylistItemInterface[]) {
    if (!Array.isArray(resources)) {
      return this;
    }
    resources.forEach((resourceInt: PlaylistItemInterface) => {
      resourceInt.timeslots = this._normalizeTimeslots(resourceInt.timeslots);
      resourceInt.weeklyScheduling = this._normalizeWeeklySchedulings(resourceInt.weeklyScheduling);
      resourceInt.calendarScheduling = this.calendarSchedulings(resourceInt.calendarScheduling);

      if (resourceInt.resource) {
        resourceInt.resource = <Resource>{ id: resourceInt.resource.id };
      }
      if (resourceInt.dcontent) {
        resourceInt.dcontent = <DatasetInterface>{ id: resourceInt.dcontent.id };
      }
      this._normalizeNested(resourceInt.nested);
    });
    return this;
  }

  private calendarSchedulings(calendarScheduling: TimeslotsCalendarInterface[]): TimeslotsCalendarInterface[] {
    if (!Array.isArray(calendarScheduling)) {
      return calendarScheduling;
    }
    calendarScheduling.forEach((calendar) => {
      if (calendar.startAt) {
        calendar.startAt = this.normalizeDate(calendar.startAt.toString());
      }
      if (calendar.endAt) {
        calendar.endAt = this.normalizeDate(calendar.endAt.toString());
      }
      calendar.weeklySchedulings = this._normalizeWeeklySchedulings(calendar.weeklySchedulings);
      calendar.timeslots = this._normalizeTimeslots(calendar.timeslots);
    });
    return calendarScheduling;
  }

  _normalizeNested(nested: PlaylistItemNestedInterface) {
    if (nested) {
      if (!Array.isArray(nested.resources)) {
        return this;
      }
      nested.resources.forEach((resourceInt: PlaylistItemInterface) => {
        // this._normalizeRange(resourceInt.range);
        // THIS SHOULDN'T EXIST IN NESTED RESOURCES
        this._normalizeTimeslots(resourceInt.timeslots);
        if (resourceInt.resource) {
          resourceInt.resource = <Resource>{ id: resourceInt.resource.id };
        }
        if (resourceInt.dcontent) {
          resourceInt.dcontent = <DatasetInterface>{ id: resourceInt.dcontent.id };
        }
      });
    }
  }

  _normalizeRange(range: DateRangeInterface[]) {
    if (!Array.isArray(range)) {
      return this;
    }
    range.forEach((rangeItem: DateRangeInterface) => {
      if (rangeItem.startAt) {
        rangeItem.startAt = this.normalizeDate(rangeItem.startAt.toString());
      }
      if (rangeItem.endAt) {
        rangeItem.endAt = this.normalizeDate(rangeItem.endAt.toString());
      }
    });
  }

  private _normalizeWeeklySchedulings(weeklyScheduling: WeeklySchedulingInterface[]): WeeklySchedulingInterface[] {
    if (!Array.isArray(weeklyScheduling)) {
      return weeklyScheduling;
    }
    weeklyScheduling.forEach((day) => {
      day.timeslots = this._normalizeTimeslots(day.timeslots);
    });
    return weeklyScheduling;
  }

  private _normalizeTimeslots(timeslots: TimeslotInterface[]): TimeslotInterface[] {
    timeslots = timeslots || [];
    timeslots.forEach((timeslot: TimeslotInterface) => {
      this._normalizeTimeslot(timeslot);
    });
    return timeslots;
  }

  private _normalizeTimeslot(timeslot: TimeslotInterface): TimeslotInterface {
    if (timeslot.from) {
      timeslot.from = this.normalizeTime(timeslot.from.toString());
    }
    if (timeslot.to) {
      timeslot.to = this.normalizeTime(timeslot.to.toString());
    }
    return timeslot;
  }
}
